import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Heart, BarChart3, Check, CreditCard, Shield, Globe, Users, Wifi, Gift, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { getCard } from '@/lib/api';
import { useCompare, useFavorites } from '@/lib/hooks';
import { toast } from 'sonner';

export default function CardDetailPage() {
  const { id } = useParams();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toggleCompare, isInCompare } = useCompare();
  const { toggleFavorite, isFavorite } = useFavorites();

  useEffect(() => {
    setLoading(true);
    getCard(id).then(r => setCard(r.data)).catch(() => toast.error('Card not found')).finally(() => setLoading(false));
  }, [id]);

  if (loading) return (
    <div className="max-w-4xl mx-auto px-6 py-12">
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-[#E4E4E7] rounded w-1/4" />
        <div className="h-40 bg-[#E4E4E7] rounded-xl" />
        <div className="grid grid-cols-4 gap-4">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-[#E4E4E7] rounded-xl" />)}</div>
      </div>
    </div>
  );

  if (!card) return (
    <div className="max-w-4xl mx-auto px-6 py-20 text-center">
      <p className="text-[#52525B] text-lg">Card not found</p>
      <Link to="/"><Button variant="outline" className="mt-4 rounded-full">Back to Cards</Button></Link>
    </div>
  );

  const inCompare = isInCompare(card.id);
  const inFavorite = isFavorite(card.id);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="card-detail-page">
      <Link to="/" className="inline-flex items-center gap-2 text-[13px] text-[#71717A] hover:text-[#09090B] mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to all cards
      </Link>

      {/* Card visual + info */}
      <div className="grid md:grid-cols-5 gap-6 mb-8 animate-fade-in-up">
        {/* Card visual */}
        <div className="md:col-span-2">
          <div className="rounded-2xl overflow-hidden aspect-[1.6/1] relative p-6 flex flex-col justify-between"
            style={{ background: `linear-gradient(135deg, ${card.bank_color}ee, ${card.bank_color}88)` }}>
            <div className="absolute top-0 right-0 w-40 h-40 rounded-full bg-white/10 -translate-y-12 translate-x-12" />
            <div className="absolute bottom-0 left-0 w-28 h-28 rounded-full bg-white/5 translate-y-8 -translate-x-8" />
            <div className="relative z-10">
              <div className="flex justify-between items-start">
                <span className="text-[11px] font-bold text-white/80 uppercase tracking-widest">{card.bank_name}</span>
                <Wifi className="w-5 h-5 text-white/50 rotate-90" />
              </div>
            </div>
            <div className="relative z-10">
              <div className="font-mono text-white/60 text-[13px] tracking-[0.25em] mb-2">
                ****&nbsp;&nbsp;****&nbsp;&nbsp;****&nbsp;&nbsp;****
              </div>
              <p className="text-white font-heading font-bold text-sm">{card.card_name}</p>
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="md:col-span-3">
          <div className="flex flex-wrap gap-2 mb-3">
            <Badge variant="outline" className="rounded-full text-[11px]">{card.card_type}</Badge>
            {card.is_islamic && <Badge variant="secondary" className="rounded-full text-[11px]">Islamic</Badge>}
            {card.contactless && <Badge variant="secondary" className="rounded-full text-[11px]">Contactless</Badge>}
          </div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#09090B] tracking-tight mb-1">{card.card_name}</h1>
          <p className="text-[13px] text-[#71717A] mb-5">{card.bank_name}</p>

          <div className="flex gap-2 mb-5">
            <Button
              variant={inFavorite ? "default" : "outline"}
              size="sm"
              data-testid="detail-favorite-btn"
              onClick={() => { toggleFavorite(card.id); toast.success(inFavorite ? 'Removed' : 'Added to favorites'); }}
              className={`rounded-full text-[12px] ${inFavorite ? 'bg-red-500 hover:bg-red-600 text-white' : ''}`}
            >
              <Heart className={`w-3.5 h-3.5 mr-1.5 ${inFavorite ? 'fill-current' : ''}`} />
              {inFavorite ? 'Saved' : 'Save'}
            </Button>
            <Button
              variant={inCompare ? "default" : "outline"}
              size="sm"
              data-testid="detail-compare-btn"
              onClick={() => { toggleCompare(card.id); toast.success(inCompare ? 'Removed' : 'Added to compare'); }}
              className={`rounded-full text-[12px] ${inCompare ? 'bg-[#002FA7] hover:bg-[#002175] text-white' : ''}`}
            >
              <BarChart3 className="w-3.5 h-3.5 mr-1.5" />
              {inCompare ? 'Comparing' : 'Compare'}
            </Button>
          </div>

          {card.welcome_bonus && (
            <div className="bg-[#FFFBEB] border border-[#FDE68A] rounded-xl px-4 py-3">
              <div className="flex items-center gap-2 mb-1">
                <Gift className="w-4 h-4 text-[#D97706]" />
                <span className="text-[11px] font-bold text-[#92400E] uppercase tracking-wider">Welcome Bonus</span>
              </div>
              <p className="text-[13px] text-[#78350F] font-medium">{card.welcome_bonus}</p>
            </div>
          )}
        </div>
      </div>

      {/* Key metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Annual Fee', value: card.annual_fee === 0 ? 'FREE' : `AED ${card.annual_fee.toLocaleString()}`, icon: CreditCard, highlight: card.annual_fee === 0 },
          { label: 'Cashback Rate', value: card.cashback_rate > 0 ? `Up to ${card.cashback_rate}%` : 'N/A', icon: Star, highlight: card.cashback_rate > 0 },
          { label: 'Interest Rate', value: card.interest_rate_monthly > 0 ? `${card.interest_rate_monthly}%/mo` : 'No Interest', icon: Globe },
          { label: 'Min Salary', value: `AED ${card.min_salary.toLocaleString()}`, icon: Users },
        ].map(m => (
          <div key={m.label} className="border border-[#E4E4E7] rounded-xl p-4 animate-fade-in-up">
            <m.icon className="w-4 h-4 text-[#A1A1AA] mb-2" />
            <span className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold block mb-1">{m.label}</span>
            <span className={`number-display font-bold text-lg ${m.highlight ? 'text-[#059669]' : 'text-[#09090B]'}`}>{m.value}</span>
          </div>
        ))}
      </div>

      {/* Details */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="border border-[#E4E4E7] rounded-xl p-6">
          <h3 className="font-heading font-bold text-[15px] text-[#09090B] mb-3">Rewards & Cashback</h3>
          <p className="text-[13px] text-[#52525B] leading-relaxed">{card.rewards_description || 'No rewards description available.'}</p>
        </div>
        <div className="border border-[#E4E4E7] rounded-xl p-6">
          <h3 className="font-heading font-bold text-[15px] text-[#09090B] mb-3">Travel & Insurance</h3>
          <div className="space-y-3 text-[13px]">
            <div>
              <span className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Lounge Access</span>
              <p className="text-[#09090B] mt-0.5">{card.lounge_access || 'None'}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Insurance</span>
              <p className="text-[#09090B] mt-0.5">{card.insurance_coverage || 'None'}</p>
            </div>
            <div>
              <span className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Foreign Transaction Fee</span>
              <p className="number-display text-[#09090B] mt-0.5">{card.foreign_transaction_fee}%</p>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="border border-[#E4E4E7] rounded-xl p-6">
          <h3 className="font-heading font-bold text-[15px] text-[#09090B] mb-3">Features</h3>
          <ul className="space-y-2">
            {card.features.map((f, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13px] text-[#52525B]">
                <Check className="w-4 h-4 text-[#059669] mt-0.5 shrink-0" />
                {f}
              </li>
            ))}
          </ul>
        </div>
        <div className="border border-[#E4E4E7] rounded-xl p-6">
          <h3 className="font-heading font-bold text-[15px] text-[#09090B] mb-3">Benefits</h3>
          <ul className="space-y-2">
            {card.benefits.map((b, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[13px] text-[#52525B]">
                <Check className="w-4 h-4 text-[#002FA7] mt-0.5 shrink-0" />
                {b}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {card.supplementary_cards > 0 && (
        <div className="mt-6 border border-[#E4E4E7] rounded-xl p-6">
          <span className="text-[10px] uppercase tracking-wider text-[#71717A] font-semibold">Supplementary Cards</span>
          <p className="number-display text-lg font-bold text-[#09090B] mt-1">Up to {card.supplementary_cards} free</p>
        </div>
      )}
    </div>
  );
}
